import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

// Components
import PageTitle from "../../layouts/PageTitle";
import SpinnerLoading from "../../components/spinner-loading/SpinnerLoading";
import SubmitButton from "../../components/ui/SubmitButton";
import FormField from "../../components/ui/FormField";
import SelectBox from "../../components/ui/SelectBox";

// Redux Actions
import { getPaymentSettingsFunc } from "../../../store/custom-actions/settings-actions/payment-gateway-settings/GetPaymentSettingsSlicer";
import { postPaymentSettingsFunc } from "../../../store/custom-actions/settings-actions/payment-gateway-settings/PostPaymentSettingsSlicer";

// Payment Gateway Configuration
const paymentGatewaysConfig = [
  {
    id: "paymob",
    title: "Paymob",
    description: "The ability to pay by paymob like VISA CARD | MOBILE WALLET",
    icon: "fa-credit-card",
    fields: [
      {
        name: "paymob_api_key",
        label: "API Key",
        type: "text",
        required: true,
      },
      { name: "paymob_hmac", label: "HMAC", type: "text", required: true },
      {
        name: "paymob_card_iframe_id",
        label: "Card Iframe ID",
        type: "text",
        required: true,
      },
      {
        name: "paymob_card_integration_id",
        label: "Card Integration ID",
        type: "text",
        required: true,
      },
      {
        name: "paymob_wallet_integration_id",
        label: "Wallet Integration ID",
        type: "text",
        required: true,
      },
      {
        name: "paymob_card_gateway",
        label: "Card Gateway",
        type: "select",
        required: true,
        options: [
          { id: 1, name: "Enable" },
          { id: 0, name: "Disable" },
        ],
      },
      {
        name: "paymob_wallet_gateway",
        label: "Wallet Gateway",
        type: "select",
        required: true,
        options: [
          { id: 1, name: "Enable" },
          { id: 0, name: "Disable" },
        ],
      },
    ],
  },
  {
    id: "fawaterk",
    title: "Fawaterk",
    description:
      "The ability to pay by fawaterk like VISA CARD | MOBILE WALLET | FAWRY | AMAN",
    icon: "fa-wallet",
    fields: [
      {
        name: "fawaterk_live_api_key",
        label: "Live API Key",
        type: "text",
        required: true,
      },
      {
        name: "fawaterk_sandbox_api_key",
        label: "Sandbox API Key",
        type: "text",
        required: true,
      },
      {
        name: "fawaterk_mode",
        label: "Mode",
        type: "select",
        required: true,
        options: [
          { id: "sandbox", name: "Sandbox" },
          { id: "live", name: "Live" },
        ],
      },
      {
        name: "fawaterk_gateway",
        label: "Gateway",
        type: "select",
        required: true,
        options: [
          { id: 1, name: "Enable" },
          { id: 0, name: "Disable" },
        ],
      },
    ],
  },
  {
    id: "paytabs",
    title: "Paytabs",
    description: "The ability to pay by paytabs like VISA CARD | APPLE PAY",
    icon: "fa-wallet",
    fields: [
      {
        name: "paytabs_live_api_key",
        label: "Live API Key",
        type: "text",
        required: true,
      },
      {
        name: "paytabs_live_profile_id",
        label: "Live Profile ID",
        type: "text",
        required: true,
      },
      {
        name: "paytabs_sandbox_api_key",
        label: "Sandbox API Key",
        type: "text",
        required: true,
      },
      {
        name: "paytabs_sandbox_profile_id",
        label: "Sandbox Profile ID",
        type: "text",
        required: true,
      },
      {
        name: "paytabs_mode",
        label: "Mode",
        type: "select",
        required: true,
        options: [
          { id: "sandbox", name: "Sandbox" },
          { id: "live", name: "Live" },
        ],
      },
      {
        name: "paytabs_gateway",
        label: "Gateway",
        type: "select",
        required: true,
        options: [
          { id: 1, name: "Enable" },
          { id: 0, name: "Disable" },
        ],
      },
    ],
  },
  {
    id: "tabby",
    title: "Tabby",
    description: "Buy now, pay later with Tabby",
    icon: "fa-calendar-days",
    fields: [
      {
        name: "tabby_live_public_key",
        label: "Live Public Key",
        type: "text",
        required: true,
      },
      {
        name: "tabby_live_secret_key",
        label: "Live Secret Key",
        type: "text",
        required: true,
      },
      {
        name: "tabby_live_merchant_code",
        label: "Live Merchant Code",
        type: "text",
        required: true,
      },
      {
        name: "tabby_sandbox_public_key",
        label: "Sandbox Public Key",
        type: "text",
        required: true,
      },
      {
        name: "tabby_sandbox_secret_key",
        label: "Sandbox Secret Key",
        type: "text",
        required: true,
      },

      {
        name: "tabby_sandbox_merchant_code",
        label: "Sandbox Merchant Code",
        type: "text",
        required: true,
      },
      {
        name: "tabby_tax_rate",
        label: "Tax Rate",
        type: "text",
        required: true,
      },
      {
        name: "tabby_mode",
        label: "Mode",
        type: "select",
        required: true,
        options: [
          { id: "sandbox", name: "Sandbox" },
          { id: "live", name: "Live" },
        ],
      },
      {
        name: "tabby_gateway",
        label: "Gateway",
        type: "select",
        required: true,
        options: [
          { id: 1, name: "Enable" },
          { id: 0, name: "Disable" },
        ],
      },
    ],
  },
  {
    id: "tamara",
    title: "Tamara",
    description: "Buy now, pay later with Tamara",
    icon: "fa-shield-halved",
    fields: [
      {
        name: "tamara_live_api_key",
        label: "Live API Key",
        type: "text",
        required: true,
      },
      {
        name: "tamara_live_notification_token",
        label: "Live Notification Token",
        type: "text",
        required: true,
      },
      {
        name: "tamara_live_merchant_id",
        label: "Live Merchant ID",
        type: "text",
        required: true,
      },
      {
        name: "tamara_sandbox_api_key",
        label: "Sandbox API Key",
        type: "text",
        required: true,
      },
      {
        name: "tamara_sandbox_notification_token",
        label: "Sandbox Notification Token",
        type: "text",
        required: true,
      },
      
      {
        name: "tamara_sandbox_merchant_id",
        label: "Sandbox Merchant ID",
        type: "text",
        required: true,
      },
      {
        name: "tamara_tax_rate",
        label: "Tax Rate",
        type: "text",
        required: true,
      },
      {
        name: "tamara_mode",
        label: "Mode",
        type: "select",
        required: true,
        options: [
          { id: "sandbox", name: "Sandbox" },
          { id: "live", name: "Live" },
        ],
      },
      {
        name: "tamara_gateway",
        label: "Gateway",
        type: "select",
        required: true,
        options: [
          { id: 1, name: "Enable" },
          { id: 0, name: "Disable" },
        ],
      },
    ],
  },
];

const paymentMethodsConfig = [
  {
    id: "cash_on_delivery",
    title: "Cash On Delivery",
    description: "The ability to pay upon receiving the product",
    icon: "fa-truck",
    fields: [
      {
        name: "cash_on_delivery",
        label: "Cash On Delivery",
        type: "select",
        required: true,
        options: [
          { id: 1, name: "Enable" },
          { id: 0, name: "Disable" },
        ],
      },
    ],
  },
  {
    id: "cash_on_site",
    title: "Cash On Site",
    description:
      "The ability to pay at an affordable price to purchase the product",
    icon: "fa-building",
    fields: [
      {
        name: "cash_on_site",
        label: "Cash On Site",
        type: "select",
        required: true,
        options: [
          { id: 1, name: "Enable" },
          { id: 0, name: "Disable" },
        ],
      },
    ],
  },
  {
    id: "send_receipt",
    title: "Send Receipt",
    description:
      "The ability to pay by sending a picture of the payment receipt",
    icon: "fa-receipt",
    fields: [
      {
        name: "send_receipt",
        label: "Send Receipt Gateway",
        type: "select",
        required: true,
        options: [
          { id: 1, name: "Enable" },
          { id: 0, name: "Disable" },
        ],
      },
    ],
  },
];

export default function PaymentsGatewaySettings() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { loading: getSettingsLoading, data } = useSelector(
    (state) => state.getPaymentSettingsSlicer
  );
  const { loading: submitLoading } = useSelector(
    (state) => state.postPaymentSettingsSlicer
  );

  const {
    register,
    setValue,
    formState: { errors },
    handleSubmit,
    watch,
  } = useForm();

  // State for individual card loading
  const [loadingCards, setLoadingCards] = useState({});

  // Load settings on component mount
  useEffect(() => {
    dispatch(getPaymentSettingsFunc());
  }, [dispatch]);

  // Set form values when data is loaded
  useEffect(() => {
    if (data) {
      // Set all payment gateway fields
      [...paymentGatewaysConfig, ...paymentMethodsConfig].forEach((gateway) => {
        gateway.fields.forEach((field) => {
          if (data[field.name] !== undefined) {
            setValue(field.name, data[field.name]);
          }
        });
      });
    }
  }, [data, setValue]);

  const onSubmitGateway = async (gatewayId, formData) => {
    setLoadingCards((prev) => ({ ...prev, [gatewayId]: true }));

    try {
      const result = await dispatch(
        postPaymentSettingsFunc({ data: formData })
      );

      if (result.payload?.success === true) {
        dispatch(getPaymentSettingsFunc());
        toast.success(
          `${t(gatewayId)} ${t("settings updated successfully!")}`,
          {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          }
        );
      } else {
        toast.error(
          `${t("Failed to update")} ${t(gatewayId)} ${t("settings")}`,
          {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          }
        );
      }
    } catch (error) {
      toast.error(`${t("Error updating")} ${t(gatewayId)} ${t("settings")}`, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setLoadingCards((prev) => ({ ...prev, [gatewayId]: false }));
    }
  };

  const renderGatewayCard = (gateway) => {
    const isCardLoading = loadingCards[gateway.id] || false;

    const handleCardSubmit = (e) => {
      e.preventDefault();
      const formData = new FormData(e.target);
      const gatewayData = {};

      // Collect only the fields for this specific gateway
      gateway.fields.forEach((field) => {
        if (formData.has(field.name)) {
          gatewayData[field.name] = formData.get(field.name);
        }
      });

      onSubmitGateway(gateway.id, gatewayData);
    };

    return (
      <div className="col-sm-12 col-md-6 col-lg-4 mb-4" key={gateway.id}>
        <div className="card">
          {/* Card Header */}
          <div className="card-header">
            <div className="d-flex align-items-center">
              <div className="me-3">
                <i className={`fa-solid ${gateway.icon} fa-2x`}></i>
              </div>
              <div>
                <h4 className="mb-1 fw-bold">{t(gateway.title)}</h4>
                <p className="mb-0 small opacity-75">
                  {t(gateway.description)}
                </p>
              </div>
            </div>
          </div>

          {/* Card Body */}
          <div className="card-body bg-white p-4">
            {gateway.id === "paymob" && (
              <div className="alert alert-info mb-3">
                <small>
                  <i className="fa fa-info-circle me-1"></i>
                  <strong>Transaction callback:</strong>{" "}
                  {process.env.REACT_APP_USERPANEL_DOMAIN}/callback
                </small>
              </div>
            )}

            <form onSubmit={handleCardSubmit}>
              <div className="row">
                {gateway.fields.map((field) => (
                  <div className="form-group mb-3 col-12" key={field.name}>
                    {field.type === "select" ? (
                      <SelectBox
                        label={field.label}
                        name={field.name}
                        register={register}
                        required={field.required}
                        errors={errors}
                        data={field.options}
                        placeholder={`Select ${field.label}`}
                        showLabel={true}
                        disabled={isCardLoading}
                      />
                    ) : (
                      <FormField
                        label={field.label}
                        name={field.name}
                        register={register}
                        required={field.required}
                        errors={errors}
                        type={field.type}
                      />
                    )}
                  </div>
                ))}
              </div>

              {/* Submit Button */}
              <div className="d-grid">
                <button
                  type="submit"
                  className="btn btn-primary btn-sm"
                  disabled={isCardLoading}
                  style={{
                    background: "linear-gradient(45deg, #667eea, #764ba2)",
                    border: "none",
                    borderRadius: "8px",
                    padding: "10px 20px",
                    fontWeight: "600",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    transition: "all 0.3s ease",
                    boxShadow: "0 4px 15px rgba(102, 126, 234, 0.3)",
                  }}
                >
                  {isCardLoading ? (
                    <>
                      <i className="fa fa-spinner fa-spin me-2"></i>
                      {t("Updating...")}
                    </>
                  ) : (
                    <>
                      <i className="fa fa-save me-2"></i>
                      {t("Update")} {t(gateway.title)}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  };

  if (getSettingsLoading) {
    return <SpinnerLoading />;
  }

  return (
    <>
      <PageTitle motherMenu="Dashboard" activeMenu="Payments Settings" />

      <div className="container-fluid">
        <div className="row">
          {paymentGatewaysConfig.map(renderGatewayCard)}
        </div>

        <div className="row">{paymentMethodsConfig.map(renderGatewayCard)}</div>
      </div>
    </>
  );
}
